/* pdp-lightbox.js — the PDP gallery's full-screen image viewer
 * (snippets/pdp-lightbox.liquid). Loaded by sections/main-product.liquid AFTER
 * product.js, whose gallery block publishes the API this reads.
 *
 * WHY A SEPARATE FILE, AND WHY IT IS NOT A SECOND CAROUSEL
 * The gallery, its index, its arrows and its counter already exist in
 * product.js. Reimplementing them here would give the PDP two sources of
 * truth for "which image am I on" and they would drift the moment a variant
 * switch moved one of them. So product.js exposes `<product-gallery>.gallery`
 * = { slides, index, goTo } and this file:
 *   - opens on gallery.index (the slide the shopper is actually looking at);
 *   - on close calls gallery.goTo(...) so the page behind lands on whatever
 *     the shopper last viewed in here.
 * It lives in its own asset rather than in product.js because product.js is a
 * hot file with several lanes open on it (price/reviews, badges, the qty
 * stepper) and because the theme already loads per-feature PDP assets this way
 * (price-match.js, purchase-block.js, dossier.js).
 *
 * IMAGES ONLY — video and 3D-model media are deliberately skipped.
 * Only image slides carry data-zoom-src, so only they get a trigger and only
 * they enter this viewer's list; the counter is n / N over IMAGES. The
 * alternative — carrying video and model slides into the overlay — was
 * rejected twice over:
 *   - a <video> would have to be MOVED out of the stage and back (losing
 *     playback position and muting state) or duplicated (two players, two
 *     buffers, both audible);
 *   - a <model-viewer> cannot be moved without re-initialising: a second
 *     instance re-downloads the GLB, and the AR / Quick Look handoff is bound
 *     to the element that was in the document when the session started.
 * Both already have a better full-size affordance in place: native video
 * controls (including the browser's own fullscreen) and model-viewer's
 * camera-controls plus "View in your room". Skipping them means the viewer
 * can never show a broken or empty frame — there is no code path that reaches
 * a non-image slide.
 *
 * PERFORMANCE
 * Nothing here fetches anything until the viewer is opened. The large
 * renditions travel as data-zoom-src / data-zoom-srcset ATTRIBUTES on the
 * slides (no <img>, no <link rel=preload>), and the frame's <img> is created
 * on first open. A PDP that is never opened pays only those attribute bytes.
 *
 * SIZING, SCROLLING AND PANNING (2026-09-14)
 * Luke, on the live theme, desktop, on a very wide short window:
 *   "when in this lightbox view, if the width of the screen makes the full
 *   height of the image not visible we need to allow the user to scroll
 *   up/down around the image in the way you'd expect, keeping [X] and
 *   [magnifying glass] controls fixed at the top corners of the screen as
 *   they are while allowing to scroll or mouse-drag around the image
 *   appropriately as you'd expect."
 *
 * The picture's box is now set in PIXELS here (--plb-w / --plb-h, consumed by
 * theme.css) instead of being left to `width:100%;height:100%;object-fit:
 * contain` plus a `max-height:min(100%, 1.8x)` clamp. That clamp was circular
 * and never bound: the frame's implicit grid row is `auto`, so it sized to the
 * image's aspect-driven height and the 100% then resolved against the row it
 * had just grown. Measured on the live theme, Studio Bagno Synthesis (natural
 * 1356x1172): a 2000x445 frame held a 2000x1728 box (74% of the picture behind
 * overflow:hidden) and a 1440x900 frame held a 1440x1244 one (28%). One scale
 * factor over the natural pixels cannot do that to itself.
 *
 * TWO STATES, ONE SCALE.
 *   fit  = min(frameW/naturalW, frameH/naturalH, MAX_UPSCALE)  -- the default.
 *          A true contain, so it can never overflow; still GROWS a small
 *          source to fill the viewport, which is what D2 asked for.
 *   zoom = fit * ZOOM_STEP -- the only state that overflows, and it overflows
 *          into a real scroll container rather than under overflow:hidden.
 * See the "why contain and not fill-width" note above applyScale().
 */
(() => {
  'use strict';

  const root = document.querySelector('[data-lightbox]');
  const gal = document.querySelector('[data-gallery]');
  if (!root || !gal) return;

  const stage = gal.querySelector('[data-stage]');
  if (!stage) return;

  // The viewer's list: image slides only, in gallery order. `galleryIndex` is
  // the index in the GALLERY (what goTo/index speak), `n` the index in here.
  //
  // Rebuilt on every open, never cached at load, because the gallery's visible
  // set is not fixed: variant-scoped media (product.js) hides the slides that
  // belong to another finish, and a shopper can change finish between two
  // opens. Reading gallery.visibleSlides() at open time is what keeps the
  // viewer's list, its counter and its arrows agreeing with the strip behind
  // it instead of drifting from it.
  const buildItems = () => {
    const api = gal.gallery;
    const visible = (api && typeof api.visibleSlides === 'function')
      ? api.visibleSlides()
      : [...stage.children];
    // `i` counts in the VISIBLE list because that is what gallery.index and
    // gallery.goTo speak (see the API note in product.js) — not the position
    // among stage.children, which includes slides variant scoping has hidden.
    return visible
      .map((el, i) => ({ el, i }))
      .filter(({ el }) => el.hasAttribute('data-zoom-src'))
      .map(({ el, i }, n) => ({
        n,
        el,
        galleryIndex: i,
        src: el.getAttribute('data-zoom-src'),
        srcset: el.getAttribute('data-zoom-srcset') || '',
        alt: el.getAttribute('data-zoom-alt') || '',
      }));
  };
  let items = buildItems();
  if (!items.length) return;

  const dialog = root.querySelector('[data-plb-dialog]');
  const frame = root.querySelector('[data-plb-frame]');
  const canvas = root.querySelector('[data-plb-canvas]') || frame;
  const prevBtn = root.querySelector('[data-plb-prev]');
  const nextBtn = root.querySelector('[data-plb-next]');
  const zoomBtn = root.querySelector('[data-plb-zoom]');
  const countEl = root.querySelector('[data-plb-count]');

  // One image, no navigation: hide the arrows outright rather than leaving two
  // permanently disabled controls in the tab order, and drop the "1 / 1"
  // counter. Re-evaluated on every open — variant scoping can take a
  // multi-image product down to a single image for one finish.
  let single = false;
  const applyCardinality = () => {
    single = items.length < 2;
    [prevBtn, nextBtn].forEach((b) => { if (b) { b.hidden = single; b.disabled = single; } });
    if (countEl) countEl.hidden = single;
  };
  applyCardinality();

  let img = null;      // created on first open — this is the performance contract
  let cur = 0;         // index into `items`
  let isOpen = false;
  let opener = null;   // the trigger to hand focus back to
  let zoomed = false;

  /* ---------- the frame image ---------- */
  const ensureImg = () => {
    if (img) return img;
    img = document.createElement('img');
    img.className = 'plb__img';
    img.decoding = 'async';
    img.draggable = false;
    // 100vw: the frame is the viewport. The browser picks from data-zoom-srcset
    // (1200/1600/2048/2560) for the device, so a phone does not pull 2560px.
    img.sizes = '100vw';
    // The size is applied as pixels the moment the pixels are known. A cached
    // image can be `complete` before this listener is ever reached, so show()
    // checks that too -- `load` alone loses the second view of the same photo.
    img.addEventListener('load', () => fitToFrame(true));
    // Ghost-drag. `draggable=false` and -webkit-user-drag:none already cover
    // Chromium and WebKit, but the hero and marquee lanes both had a native
    // drag image survive them, and a ghost mid-pan looks like the viewer broke.
    // Cancelling dragstart is the one that holds everywhere.
    img.addEventListener('dragstart', (e) => e.preventDefault());
    canvas.appendChild(img);
    return img;
  };

  /* ---------- scale, scroll and pan ----------
   * WHY THE DEFAULT IS CONTAIN AND NOT FILL-WIDTH.
   * Luke's report is about being unable to reach the rest of a picture, and
   * there are two ways to answer it: keep filling the width and make the
   * overflow scrollable, or stop overflowing by default and keep the scroll
   * for the zoom. We chose contain, for three reasons.
   *   1. Overflow-by-default hides content with no signal. The viewer opens
   *      mid-picture, and the only hint that there is more is a scrollbar we
   *      deliberately do not draw. A shopper who does not try to scroll simply
   *      never sees the bottom of the basin -- which is the report.
   *   2. It is not only an extreme-aspect problem. Filling the width crops
   *      every portrait photo at 1440x900 as well (measured: 28%). Contain
   *      fixes the report at every viewport at once; scroll-the-overflow fixes
   *      it only for people who discover the scroll.
   *   3. Every image viewer a shopper has used -- Preview, Photos, Lightroom
   *      web, PhotoSwipe, Fancybox -- opens contained. Opening cropped is the
   *      surprising choice, and D2's own words are "a full page photo", not a
   *      full-width detail.
   * The cost is honest and stated in D2: on a 4.5:1 window the picture is only
   * as tall as the window, so it looks small. The magnifier is the answer to
   * that and it is one click away, top left, and it now pans properly.
   *
   * MECHANISM. Zoom used to be `transform:scale(2.5)` with transform-origin
   * chasing the mouse. A transform does not make a scroll container scroll, it
   * needs a hovering cursor (so it did nothing for a trackpad user who wanted
   * to wheel), and it has no keyboard or touch equivalent. Scaling the BOX
   * instead gives wheel, trackpad, keyboard, touch and momentum for free from
   * the browser's own scrolling, and the drag below is the only thing left to
   * write by hand. */
  const MAX_UPSCALE = 1.8;  // D2 decision 7, unchanged: never past 1.8x the source
  const ZOOM_STEP = 2.5;    // what the old transform used, kept so zoom feels the same
  let fitScale = 1;
  let curScale = 1;

  const reduceMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const measure = () => {
    if (!img || !img.naturalWidth || !img.naturalHeight) return null;
    const fw = frame.clientWidth, fh = frame.clientHeight;
    if (!fw || !fh) return null;  // still hidden: nothing to size against yet
    return { nw: img.naturalWidth, nh: img.naturalHeight, fw, fh };
  };

  // Cap the upscale. A lot of this catalogue's renders are small originals
  // (the Caroma set is 540px square) and blowing one of those up is a soft
  // picture pretending to be a detailed one. 1.8x is as far as a source is
  // allowed to be stretched to FIT; a deliberate zoom is allowed past it,
  // because that is the one moment the shopper has asked to see the grain.
  const computeFit = (m) => Math.min(m.fw / m.nw, m.fh / m.nh, MAX_UPSCALE);

  // `is-pannable` drives the grab cursor, the keyboard pan and the swipe
  // suspension. Read off the frame's own geometry rather than off `zoomed`, so
  // it stays true no matter what makes the picture bigger than the frame.
  const syncPannable = () => {
    const pan = frame.scrollHeight > frame.clientHeight + 1 ||
                frame.scrollWidth > frame.clientWidth + 1;
    root.classList.toggle('is-pannable', pan);
    return pan;
  };
  const isPannable = () => root.classList.contains('is-pannable');

  /* Apply a scale, optionally holding one client point still.
   * `anchor` is where the cursor or the tap was: the fraction of the picture
   * under it is measured before the resize and restored after, so zooming in
   * on the tap handle keeps the tap handle under your finger instead of
   * jumping to the middle. Clamped to [0,1] so a click in the letterbox beside
   * the picture anchors on its edge rather than flinging the scroll. */
  const applyScale = (scale, anchor) => {
    const m = measure();
    if (!m) return;
    let fx = 0.5, fy = 0.5;
    const before = img.getBoundingClientRect();
    if (anchor && before.width && before.height) {
      fx = Math.min(1, Math.max(0, (anchor.x - before.left) / before.width));
      fy = Math.min(1, Math.max(0, (anchor.y - before.top) / before.height));
    }
    curScale = scale;
    root.style.setProperty('--plb-w', Math.round(m.nw * scale) + 'px');
    root.style.setProperty('--plb-h', Math.round(m.nh * scale) + 'px');
    if (anchor) {
      // reading the rect forces the layout the two properties above just
      // invalidated, so this is the NEW geometry, not last frame's.
      const after = img.getBoundingClientRect();
      frame.scrollLeft += (after.left + fx * after.width) - anchor.x;
      frame.scrollTop += (after.top + fy * after.height) - anchor.y;
    }
    syncPannable();
  };

  const fitToFrame = (resetScroll) => {
    const m = measure();
    if (!m) return;
    fitScale = computeFit(m);
    applyScale(fitScale, null);
    if (resetScroll !== false) { frame.scrollLeft = 0; frame.scrollTop = 0; }
    syncPannable();
  };

  const setZoom = (on, anchor) => {
    const m = measure();
    zoomed = !!on && !!m;
    root.classList.toggle('is-zoomed', zoomed);
    if (m) {
      fitScale = computeFit(m);
      if (zoomed) {
        applyScale(fitScale * ZOOM_STEP, anchor);
      } else {
        applyScale(fitScale, null);
        frame.scrollLeft = 0;  // zooming out returns to fit AND to the top-left
        frame.scrollTop = 0;
      }
    }
    syncPannable();
    if (zoomBtn) {
      zoomBtn.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
      zoomBtn.setAttribute('aria-label', zoomed ? 'Zoom out' : 'Zoom in. Then drag or scroll to pan');
    }
  };

  /* Mouse/pen drag-to-pan. Touch is deliberately NOT handled here: the frame is
   * `touch-action: pan-x pan-y`, so a finger pans it natively, with momentum
   * and rubber-banding this could not reproduce. Pointer capture on the frame
   * keeps the drag alive when the cursor leaves the window mid-throw. */
  let drag = null;
  let suppressClick = false;

  frame.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'touch') return;      // native scrolling owns the finger
    if (e.button !== 0) return;
    if (e.target.closest('button')) return;
    if (!isPannable()) return;                  // nothing to pan: let the click zoom
    drag = { id: e.pointerId, x: e.clientX, y: e.clientY,
             sl: frame.scrollLeft, st: frame.scrollTop, moved: false };
    root.classList.add('is-panning');
    try { frame.setPointerCapture(e.pointerId); } catch (_) { /* older engines */ }
    e.preventDefault();                          // no text/image selection drag
  });

  frame.addEventListener('pointermove', (e) => {
    if (!drag || e.pointerId !== drag.id) return;
    const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
    // 3px of slack: a click is never perfectly still, and a 1px twitch must not
    // turn "click to zoom out" into "you dragged, nothing happens".
    if (!drag.moved && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) drag.moved = true;
    frame.scrollLeft = drag.sl - dx;             // drag the picture, not the window
    frame.scrollTop = drag.st - dy;
  });

  const endDrag = () => {
    if (!drag) return;
    suppressClick = drag.moved;                  // a drag is not a click to zoom
    try { frame.releasePointerCapture(drag.id); } catch (_) { /* already gone */ }
    drag = null;
    root.classList.remove('is-panning');
  };
  frame.addEventListener('pointerup', endDrag);
  frame.addEventListener('pointercancel', endDrag);

  // touch: a one-finger drag is native panning (above). What is left here is
  // the swipe that changes image -- and it must NOT fire while the picture is
  // pannable, or every attempt to look at the bottom of a photo would land on
  // the next one. In that state the arrows and the arrow keys are navigation.
  let t0 = null;
  frame.addEventListener('touchstart', (e) => {
    if (e.touches.length !== 1) { t0 = null; return; }
    t0 = { x: e.touches[0].clientX, y: e.touches[0].clientY, moved: false };
  }, { passive: true });

  frame.addEventListener('touchmove', (e) => {
    if (!t0 || e.touches.length !== 1) return;
    const dx = e.touches[0].clientX - t0.x;
    const dy = e.touches[0].clientY - t0.y;
    if (Math.abs(dx) > 8 || Math.abs(dy) > 8) t0.moved = true;
  }, { passive: true });   // passive: the browser is doing the panning now

  frame.addEventListener('touchend', (e) => {
    if (!t0) return;
    const end = e.changedTouches && e.changedTouches[0];
    const dx = end ? end.clientX - t0.x : 0;
    const dy = end ? end.clientY - t0.y : 0;
    const t = t0; t0 = null;
    if (isPannable()) return;                    // that drag was a pan
    if (!t.moved) { setZoom(true, end ? { x: end.clientX, y: end.clientY } : null); return; }
    if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) go(cur + (dx < 0 ? 1 : -1));
  }, { passive: true });

  // pointer click toggles zoom, but not a click that was really a touch tap
  // (touchend already handled it), not a click on a control, and not the
  // release of a pan.
  frame.addEventListener('click', (e) => {
    if (suppressClick) { suppressClick = false; return; }
    if (e.detail === 0) return;                 // keyboard-synthesised click
    if (e.target.closest('button')) return;
    setZoom(!zoomed, { x: e.clientX, y: e.clientY });
  });

  // A rotate or a window resize changes the frame, so the fit changes with it.
  // Hold the centre of what is on screen rather than snapping to the top-left,
  // which is what a zoomed-in shopper rotating a phone expects.
  let resizeRaf = 0;
  window.addEventListener('resize', () => {
    if (!isOpen || !img) return;
    cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(() => {
      const m = measure();
      if (!m) return;
      const oldW = m.nw * curScale, oldH = m.nh * curScale;
      const fx = oldW ? (frame.scrollLeft + frame.clientWidth / 2) / oldW : 0.5;
      const fy = oldH ? (frame.scrollTop + frame.clientHeight / 2) / oldH : 0.5;
      fitScale = computeFit(m);
      applyScale(zoomed ? fitScale * ZOOM_STEP : fitScale, null);
      frame.scrollLeft = fx * (m.nw * curScale) - frame.clientWidth / 2;
      frame.scrollTop = fy * (m.nh * curScale) - frame.clientHeight / 2;
      syncPannable();
    });
  });

  /* ---------- navigation ---------- */
  const show = (n) => {
    cur = (n + items.length) % items.length;   // wrap: the reference wraps
    const it = items[cur];
    const el = ensureImg();
    setZoom(false);
    if (it.srcset) el.srcset = it.srcset;
    el.src = it.src;
    el.alt = it.alt;
    // A new photo always starts fitted at the top-left. `load` does this, but
    // it does not fire for an image already in the cache (paging back and
    // forth through a gallery is nothing but cache hits), so a complete image
    // is sized here and now -- otherwise the second view of a photo keeps the
    // previous one's box and is the wrong size until something else resizes it.
    if (el.complete && el.naturalWidth) fitToFrame(true);
    if (countEl) countEl.textContent = (cur + 1) + ' / ' + items.length;
  };
  const go = (n) => { if (!single) show(n); };

  if (prevBtn) prevBtn.addEventListener('click', () => go(cur - 1));
  if (nextBtn) nextBtn.addEventListener('click', () => go(cur + 1));
  // the button has no meaningful cursor position of its own (it lives in the
  // corner), so it zooms about the centre of the frame.
  if (zoomBtn) zoomBtn.addEventListener('click', () => {
    const r = frame.getBoundingClientRect();
    setZoom(!zoomed, { x: r.left + r.width / 2, y: r.top + r.height / 2 });
  });

  /* ---------- open / close ----------
   * Modal contract: role=dialog + aria-modal on the panel (in the snippet),
   * focus moved in on open and returned to the trigger on close, Tab trapped,
   * Escape closes, background scroll locked with .drawer-open — the same
   * body class the cart drawer and the price-match dialog use, so two
   * overlays can never fight over the lock's bookkeeping.
   */
  /* Scroll lock. `.drawer-open` (body { overflow:hidden }) is the theme's
   * existing class and is kept so anything keyed on it still fires, but on its
   * own it DOES NOT stop the page: <html> is the scrolling element on the PDP,
   * and body overflow has no effect on it. Measured on the ci preview
   * 2026-09-10 — with the viewer open, a wheel took the page from scrollY 31
   * to 1217 at 1440 and 0 to 1200 at 390 while body's computed overflow read
   * `hidden`. Locking the ROOT is what actually holds.
   * The scrollbar it removes would widen the content and score a layout shift,
   * so its width is handed back as padding on the same element: net zero. */
  const lockScroll = () => {
    const sbw = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--plb-sbw', (sbw > 0 ? sbw : 0) + 'px');
    document.documentElement.classList.add('plb-locked');
    document.body.classList.add('drawer-open');
  };
  const unlockScroll = () => {
    document.documentElement.classList.remove('plb-locked');
    document.documentElement.style.removeProperty('--plb-sbw');
    document.body.classList.remove('drawer-open');
  };

  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([type="hidden"]),select,textarea,[tabindex]:not([tabindex="-1"])';
  const focusables = () => [...dialog.querySelectorAll(FOCUSABLE)].filter((el) => !el.hidden && el.offsetParent !== null);

  const open = (galleryIndex, from) => {
    if (isOpen) return;
    items = buildItems();
    if (!items.length) return;
    applyCardinality();
    const start = items.findIndex((it) => it.galleryIndex === galleryIndex);
    opener = from || document.activeElement;
    // UNHIDE BEFORE show(). The frame has no clientWidth/clientHeight while the
    // overlay is `hidden`, and the fit is computed from exactly those two
    // numbers -- sizing first would divide by a zero-sized frame and leave the
    // picture at whatever the last open left behind.
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    show(start > -1 ? start : 0);
    lockScroll();
    requestAnimationFrame(() => root.classList.add('is-open'));
    isOpen = true;
    // focus the panel itself, not the first control: the shopper came here to
    // look at a picture, and landing on Close reads as "you are about to leave".
    // Tab from the panel then walks zoom -> close -> prev -> next.
    setTimeout(() => dialog.focus(), 30);
  };

  const close = () => {
    if (!isOpen) return;
    isOpen = false;
    setZoom(false);
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    root.hidden = true;
    unlockScroll();
    // put the page behind on the image the shopper left the viewer on. 'auto':
    // the gallery is off-screen behind an overlay that is going away this
    // frame, so a smooth scroll would animate something nobody sees.
    const api = gal.gallery;
    if (api && typeof api.goTo === 'function') api.goTo(items[cur].galleryIndex, 'auto');
    // hand focus back to the trigger ON that slide, so Tab resumes where the
    // shopper's eye is — not on the trigger of the image they opened from.
    const landed = items[cur].el || null;
    const back = (landed && landed.querySelector('[data-plb-open]')) || opener;
    // preventScroll: focus() otherwise scrolls its element into view on BOTH
    // axes. Vertically that yanks the page (the shopper may have scrolled far
    // past the gallery); horizontally it fights the goTo above and won — at
    // 390 the gallery closed one slide past the one the viewer was showing
    // (counter 3/13 in the viewer, 4/13 behind it). Measured 2026-09-10.
    if (back && document.contains(back) && typeof back.focus === 'function') {
      try { back.focus({ preventScroll: true }); } catch { back.focus(); }
    }
    opener = null;
  };

  // triggers: the magnifier, and the image itself (a click anywhere on an
  // image slide opens — the reference does this and it is what shoppers try).
  gal.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-plb-open]');
    const slide = e.target.closest('.pgal__slide[data-zoom-src]');
    if (!btn && !slide) return;
    // let the arrows/thumbs keep their own clicks
    if (!btn && e.target.closest('[data-gprev],[data-gnext],[data-thumb]')) return;
    e.preventDefault();
    const host = (btn && btn.closest('.pgal__slide')) || slide;
    const api = gal.gallery;
    const visible = (api && typeof api.visibleSlides === 'function') ? api.visibleSlides() : [...stage.children];
    const idx = visible.indexOf(host);
    if (idx < 0) return; // a hidden slide cannot be clicked, but never guess
    open(idx, btn || host.querySelector('[data-plb-open]'));
  });

  root.addEventListener('click', (e) => { if (e.target.closest('[data-plb-close]')) { e.preventDefault(); close(); } });

  document.addEventListener('keydown', (e) => {
    if (!isOpen) return;
    if (e.key === 'Escape') { e.preventDefault(); if (zoomed) setZoom(false); else close(); return; }
    /* Up/Down pan; Left/Right navigate. The brief asked for "the keyboard
     * arrows pan it", and the vertical pair is given over to panning outright
     * -- they did nothing at all before, and vertical is the axis the report
     * is about. Left/Right are NOT: they are the only keyboard route to the
     * next photo, they match the two arrows pinned to the sides of the screen,
     * and on touch they are the navigation that replaces the swipe the moment
     * the picture becomes pannable. Horizontal panning has the wheel, the
     * trackpad and the drag; the next photo would have nothing. */
    if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && isPannable()) {
      e.preventDefault();
      const step = Math.max(64, frame.clientHeight * 0.5);
      frame.scrollBy({ top: e.key === 'ArrowDown' ? step : -step,
                       behavior: reduceMotion() ? 'auto' : 'smooth' });
      return;
    }
    if (e.key === 'ArrowRight') { e.preventDefault(); go(cur + 1); return; }
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(cur - 1); return; }
    if (e.key !== 'Tab') return;
    const list = focusables();
    if (!list.length) { e.preventDefault(); dialog.focus(); return; }
    const first = list[0], last = list[list.length - 1];
    const inside = dialog.contains(document.activeElement) && document.activeElement !== dialog;
    if (e.shiftKey && (document.activeElement === first || !inside)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (document.activeElement === last || !inside)) { e.preventDefault(); first.focus(); }
  });
})();
